import { useLingui } from '@lingui/react/macro'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useRuntimeVariables } from 'app-kit'
import { LucideIcon } from 'components/LucideIcon'
import { useGeckoAuth } from 'features/auth'
import { useOpenEvaluationsTab } from 'features/evaluations/hooks/useOpenEvaluationsTab'
import { EVALUATIONS_PERMISSIONS } from 'features/evaluations/perms/evaluationsPermissions'
import { EmailSupport } from 'features/navbar/EmailSupport'
import { KeyboardShortcutsToggle } from 'features/navbar/KeyboardShortcutsToggle'
import { LanguageToggle } from 'features/navbar/LanguageToggle'
import { NavbarButtonContainer } from 'features/navbar/NavbarButtonContainer'
import {
  DropdownLink,
  StyledDropdownMenuContent,
  StyledDropdownMenuTrigger,
} from 'features/navbar/NavbarDropdown'
import { PermissionGateToggle } from 'features/navbar/PermissionGateToggle'
import { useSiteSelectorCapabilities } from 'features/navbar/siteSelectors/siteSelectorCapabilities'
import { ThemeModeToggle } from 'features/navbar/ThemeModeToggle'
import { UnitsToggle } from 'features/navbar/UnitsToggle'
import { PermissionsGate } from 'features/permissions'
import mixpanel from 'mixpanel-browser'
import React from 'react'
import { createAccessSearch } from 'routes/access/-searchParams'
import styled from 'styled-components'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

import { ProfileAndSettingsBase } from './ProfileAndSettingsBase'
import { type ProfileAndSettingsProps } from './useProfileAndSettingsState'

const ProfilePicFallback = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  width: 18px;
`

const DropdownNonLink = styled(DropdownMenu.Item)`
  padding: 0.25rem 1rem;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.secondaryText};

  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

const EvaluationsTopBarItem: React.FC = () => {
  const { t } = useLingui()
  const handleSelect = useOpenEvaluationsTab()

  return (
    <DropdownNonLink onClick={handleSelect}>{t`Evaluations`}</DropdownNonLink>
  )
}

const LogOutButton: React.FC = () => {
  const { t } = useLingui()
  const { logout } = useGeckoAuth()
  const { BASE_PATH } = useRuntimeVariables()
  return (
    <DropdownMenu.Item
      onClick={() => {
        mixpanel.reset()
        logout({
          returnTo: `${window.location.origin}${BASE_PATH}`,
        })
      }}>
      <div style={{ padding: '0.25rem 1rem', cursor: 'pointer' }}>
        <a>{t`Log out`}</a>
      </div>
    </DropdownMenu.Item>
  )
}

export const ProfileAndSettingsTopBar: React.FC<ProfileAndSettingsProps> = ({
  orgID,
  siteID,
  onOpenNotificationPreferences,
  showNotificationPreferences = true,
}) => {
  const { t } = useLingui()
  const { canManageAccess } = useSiteSelectorCapabilities(orgID, siteID)
  return (
    <ProfileAndSettingsBase
      renderTrigger={(state) => (
        <StyledDropdownMenuTrigger asChild>
          <NavbarButtonContainer
            $isSelected={state.isModalOpen}
            aria-label={t`Open profile settings menu`}
            aria-expanded={state.isModalOpen}
            onClick={() => state.setIsModalOpen((open) => !open)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                state.setIsModalOpen((open) => !open)
              }
            }}>
            <ProfilePicFallback>
              <LucideIcon name='User' size={18} />
            </ProfilePicFallback>
          </NavbarButtonContainer>
        </StyledDropdownMenuTrigger>
      )}
      renderContent={(state) => (
        <DropdownMenu.Portal>
          <StyledDropdownMenuContent sideOffset={12} align='end'>
            {state.shouldShowUnits && <UnitsToggle />}
            <LanguageToggle />
            <ThemeModeToggle />
            <KeyboardShortcutsToggle />
            {showNotificationPreferences ? (
              <DropdownNonLink onClick={onOpenNotificationPreferences}>
                {t`Manage Notifications`}
              </DropdownNonLink>
            ) : null}
            <DropdownNonLink onClick={() => state.setShowToSModal(true)}>
              {t`Terms of Service`}
            </DropdownNonLink>
            <EmailSupport />
            <PermissionsGate requires={EVALUATIONS_PERMISSIONS}>
              <EvaluationsTopBarItem />
            </PermissionsGate>
            <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
              <PermissionGateToggle />
            </PermissionsGate>
            <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
              <DropdownLink to='/design-system-library'>
                {t`Design System Library`}
              </DropdownLink>
            </PermissionsGate>
            {canManageAccess ? (
              <DropdownLink
                to='/access'
                search={createAccessSearch({ orgId: orgID, siteId: siteID })}>
                {t`Manage Site Access`}
              </DropdownLink>
            ) : null}
            <LogOutButton />
          </StyledDropdownMenuContent>
        </DropdownMenu.Portal>
      )}
    />
  )
}
